import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useCompany } from './CompanyContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LicenseContext = createContext({
  activeModules: [],
  payrollSubModules: [],
  isModuleLicensed: () => true,
  refreshLicense: () => {},
  loading: true,
});

// Map nav paths to module codes
const PATH_MODULE_MAP = {
  '/cost-accounting': 'CA2',
  '/payroll': 'PR',
  '/payroll-reports': 'PR',
  '/tax-setup': 'PR',
  '/view/enterprises': 'CA2',
  '/view/equipment': 'CA2',
  '/transactions/ap': 'AP',
  '/transactions/ar': 'AR',
  '/transactions/paychecks': 'PR',
  '/transactions/timecards': 'PR',
  '/tools/bank-reconciliation': 'CR',
};

export const LicenseProvider = ({ children }) => {
  const { currentCompany } = useCompany();
  const companyId = currentCompany?.company_id || 'default';
  const [activeModules, setActiveModules] = useState([]);
  const [payrollSubModules, setPayrollSubModules] = useState([]);
  const [loading, setLoading] = useState(true);

  const refreshLicense = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/licensing/active-modules?company_id=${companyId}`);
      if (res.ok) {
        const data = await res.json();
        setActiveModules(data.active_modules || []);
        setPayrollSubModules(data.payroll_sub_modules || []);
      }
    } catch (e) {
      console.error('Failed to fetch license:', e);
    }
    setLoading(false);
  }, [companyId]);

  useEffect(() => { refreshLicense(); }, [refreshLicense]);

  const isModuleLicensed = useCallback((moduleCode) => {
    // If no modules are activated at all, allow everything (unlicensed = full access for demo)
    if (activeModules.length === 0) return true;
    return activeModules.includes(moduleCode);
  }, [activeModules]);

  const isPathLicensed = useCallback((path) => {
    const moduleCode = PATH_MODULE_MAP[path];
    if (!moduleCode) return true; // No module mapping = always allowed
    return isModuleLicensed(moduleCode);
  }, [isModuleLicensed]);

  return (
    <LicenseContext.Provider value={{
      activeModules,
      payrollSubModules,
      isModuleLicensed,
      isPathLicensed,
      refreshLicense,
      loading,
    }}>
      {children}
    </LicenseContext.Provider>
  );
};

export const useLicense = () => useContext(LicenseContext);
